import {
  defaultGenerationOptions,
  type CreateGenerationProjectInput,
  type GenerationAttachmentRole,
  type GenerationFollowUp,
  type GenerationGoal,
  type GenerationOptions,
  type GenerationRouteStep,
  type PreviewRouteInput,
} from '@mpr/shared';

import type { GenerationCapability, GenerationConnector, GenerationJob } from './api.js';
import type { OutputChain } from './studioProject.js';
import type { TranslationKey } from '../i18n/translations.js';

/**
 * The goal-first studio's logic that has no screen of its own: which choices
 * matter for a goal, how the user's changes sit on top of the documented
 * defaults, what the server is sent, and what a project's next step is. Kept
 * apart from the components so each rule can be tested without rendering.
 */

/* -------------------------------------------------------------------------- */
/*  Goals                                                                      */
/* -------------------------------------------------------------------------- */

/** The order the goal cards are shown in. */
export const GOAL_ORDER: GenerationGoal[] = [
  'POST',
  'IMAGE_PROMPT',
  'VIDEO_FROM_TEXT',
  'VIDEO_FROM_IMAGE',
  'AUDIO_TEXT',
];

/** Only a post can carry a picture or video planned in the same request. */
export function normalizeFollowUp(goal: GenerationGoal, followUp: GenerationFollowUp): GenerationFollowUp {
  return goal === 'POST' ? followUp : 'NONE';
}

/** The request needs a picture to work from: the video animates it. */
export function needsImage(goal: GenerationGoal, followUp: GenerationFollowUp): boolean {
  return goal === 'VIDEO_FROM_IMAGE' || (goal === 'POST' && followUp === 'VIDEO_FROM_IMAGE');
}

const wantsVideo = (goal: GenerationGoal, followUp: GenerationFollowUp) =>
  goal === 'VIDEO_FROM_TEXT' ||
  goal === 'VIDEO_FROM_IMAGE' ||
  (goal === 'POST' && (followUp === 'VIDEO_FROM_TEXT' || followUp === 'VIDEO_FROM_IMAGE'));

const wantsImage = (goal: GenerationGoal, followUp: GenerationFollowUp) =>
  goal === 'IMAGE_PROMPT' || (goal === 'POST' && followUp === 'IMAGE');

/* -------------------------------------------------------------------------- */
/*  Options                                                                    */
/* -------------------------------------------------------------------------- */

export type OptionGroup = 'language' | 'textOnMedia' | 'post' | 'image' | 'video' | 'audio';

/** Which groups of choices apply to a request; the rest are not shown or sent. */
export function relevantGroups(goal: GenerationGoal, followUp: GenerationFollowUp): OptionGroup[] {
  const groups: OptionGroup[] = ['language'];
  if (goal === 'POST') groups.push('post');
  if (wantsImage(goal, followUp) || wantsVideo(goal, followUp)) groups.push('textOnMedia');
  if (wantsImage(goal, followUp)) groups.push('image');
  if (wantsVideo(goal, followUp)) groups.push('video');
  if (goal === 'AUDIO_TEXT') groups.push('audio');
  return groups;
}

/** Diacritics only matter for words that will be spoken. */
export function diacriticsApply(goal: GenerationGoal, followUp: GenerationFollowUp): boolean {
  return goal === 'AUDIO_TEXT' || wantsVideo(goal, followUp);
}

const OBJECT_GROUPS = ['textOnMedia', 'post', 'image', 'video', 'audio'] as const;

/** Drop keys whose value is undefined, so a patch never erases a default by omission. */
function defined<T extends object>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as T;
}

/**
 * The user's choices laid over a base. A group (video, post…) merges key by
 * key, so changing the aspect keeps every other video default; anything else
 * (language, the attachment list) is replaced.
 */
export function mergeOptions(base: GenerationOptions, patch: GenerationOptions): GenerationOptions {
  const out: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    if ((OBJECT_GROUPS as readonly string[]).includes(key) && typeof value === 'object' && value !== null) {
      out[key] = { ...((base as Record<string, object | undefined>)[key] ?? {}), ...defined(value) };
    } else {
      out[key] = value;
    }
  }
  return out as GenerationOptions;
}

/**
 * The defaults for a goal. A post that brings a picture or video along also
 * gets that product's own defaults (aspect, duration, pace…), because the
 * shared defaults for a post know nothing about them.
 */
export function defaultsFor(goal: GenerationGoal, followUp: GenerationFollowUp): GenerationOptions {
  const base = defaultGenerationOptions(goal);
  if (goal !== 'POST' || followUp === 'NONE') return base;
  const extra = defaultGenerationOptions(followUp === 'IMAGE' ? 'IMAGE_PROMPT' : 'VIDEO_FROM_TEXT');
  return mergeOptions(extra, base);
}

/** What survives choosing a different goal: who it is for, not how it is made. */
export function carryOptions(options: GenerationOptions): GenerationOptions {
  return defined({
    language: options.language,
    dialect: options.dialect,
    extraInstructions: options.extraInstructions,
    attachments: options.attachments,
  });
}

/* -------------------------------------------------------------------------- */
/*  Attachments                                                                */
/* -------------------------------------------------------------------------- */

/** One reference as the form holds it: an uploaded file or a pasted link, and what it is for. */
export interface AttachmentDraft {
  key: string;
  source: 'file' | 'url';
  /** A file already on the account (uploaded here). */
  mediaId?: string;
  /** A public https link. */
  url?: string;
  /** What to call it in the list. */
  label: string;
  mimeType?: string;
  uploading?: boolean;
  role: GenerationAttachmentRole;
}

export function isHttpsUrl(text: string): boolean {
  try {
    return new URL(text.trim()).protocol === 'https:';
  } catch {
    return false;
  }
}

/** The first picture of a video made from images is its opening frame; the rest are references. */
export function defaultRoleFor(
  goal: GenerationGoal,
  followUp: GenerationFollowUp,
  alreadyAttached: number,
): GenerationAttachmentRole {
  return needsImage(goal, followUp) && alreadyAttached === 0 ? 'FIRST_FRAME' : 'REFERENCE';
}

export type RequestProblem =
  | 'REQUEST_EMPTY'
  | 'UPLOADING'
  | 'BAD_URL'
  | 'NEEDS_IMAGE'
  | 'EXACT_TEXT_MISSING';

/**
 * Everything that must be put right before the request can be sent. An
 * uploaded file counts as an image unless it is known to be something else; a
 * link cannot be inspected, so it counts.
 */
export function requestProblems(input: {
  goal: GenerationGoal;
  followUp: GenerationFollowUp;
  requestText: string;
  options: GenerationOptions;
  attachments: AttachmentDraft[];
}): RequestProblem[] {
  const { goal, followUp, requestText, options, attachments } = input;
  const problems: RequestProblem[] = [];
  if (requestText.trim().length === 0) problems.push('REQUEST_EMPTY');
  if (attachments.some((a) => a.uploading)) problems.push('UPLOADING');
  if (attachments.some((a) => a.source === 'url' && !isHttpsUrl(a.url ?? ''))) problems.push('BAD_URL');
  if (
    needsImage(goal, followUp) &&
    !attachments.some(
      (a) => !a.uploading && (a.source === 'url' || !a.mimeType || a.mimeType.startsWith('image/')),
    )
  ) {
    problems.push('NEEDS_IMAGE');
  }
  const shows = wantsImage(goal, followUp) || wantsVideo(goal, followUp);
  if (shows && options.textOnMedia?.mode === 'EXACT' && !(options.textOnMedia.text ?? '').trim()) {
    problems.push('EXACT_TEXT_MISSING');
  }
  return problems;
}

/** The finished references in the shape the server takes. */
export function toOptionAttachments(drafts: AttachmentDraft[]): NonNullable<GenerationOptions['attachments']> {
  const refs: NonNullable<GenerationOptions['attachments']> = [];
  for (const a of drafts) {
    if (a.uploading) continue;
    if (a.source === 'file' && a.mediaId) refs.push({ mediaId: a.mediaId, role: a.role });
    else if (a.source === 'url' && a.url) refs.push({ url: a.url.trim(), role: a.role });
  }
  return refs;
}

/* -------------------------------------------------------------------------- */
/*  What is sent                                                               */
/* -------------------------------------------------------------------------- */

function trimmed(value: string | undefined): string | undefined {
  const text = value?.trim();
  return text ? text : undefined;
}

function clamp(value: number | undefined, min: number, max: number, whole: boolean): number | undefined {
  if (value === undefined || !Number.isFinite(value)) return undefined;
  const bounded = Math.min(max, Math.max(min, value));
  return whole ? Math.round(bounded) : bounded;
}

/**
 * The choices to send: only the groups that apply to this request, no empty
 * strings, and the references from the list. Anything the user left alone is
 * still sent — the form starts from the defaults, so the server and the
 * screen cannot disagree about what "default" meant.
 */
export function pruneOptions(
  options: GenerationOptions,
  goal: GenerationGoal,
  followUp: GenerationFollowUp,
  attachments: AttachmentDraft[],
): GenerationOptions {
  const groups = relevantGroups(goal, followUp);
  const out: GenerationOptions = defined({
    language: options.language,
    dialect: options.dialect,
    extraInstructions: trimmed(options.extraInstructions),
    diacritics: diacriticsApply(goal, followUp) ? options.diacritics : undefined,
  });

  if (groups.includes('textOnMedia') && options.textOnMedia) {
    const { mode, text } = options.textOnMedia;
    out.textOnMedia = mode === 'EXACT' ? defined({ mode, text: trimmed(text) }) : { mode };
  }
  if (groups.includes('post') && options.post) out.post = defined(options.post);
  if (groups.includes('image') && options.image) {
    out.image = defined({ ...options.image, style: trimmed(options.image.style) });
  }
  if (groups.includes('video') && options.video) {
    // A typed number can stray outside what the server accepts; keep it inside.
    out.video = defined({
      ...options.video,
      durationSeconds: clamp(options.video.durationSeconds, 2, 180, true),
      scenes: clamp(options.video.scenes, 1, 30, true),
    });
  }
  if (groups.includes('audio') && options.audio) {
    out.audio = defined({
      ...options.audio,
      voice: trimmed(options.audio.voice),
      speed: clamp(options.audio.speed, 0.5, 2, false),
    });
  }

  const refs = toOptionAttachments(attachments);
  if (refs.length > 0) out.attachments = refs;
  return out;
}

export function buildPreviewBody(input: {
  goal: GenerationGoal;
  followUp: GenerationFollowUp;
  options: GenerationOptions;
  attachments: AttachmentDraft[];
}): PreviewRouteInput {
  const followUp = normalizeFollowUp(input.goal, input.followUp);
  return {
    goal: input.goal,
    followUp,
    options: pruneOptions(input.options, input.goal, followUp, input.attachments),
  };
}

export function buildCreateBody(input: {
  goal: GenerationGoal;
  followUp: GenerationFollowUp;
  requestText: string;
  options: GenerationOptions;
  attachments: AttachmentDraft[];
}): Pick<CreateGenerationProjectInput, 'requestText' | 'goal' | 'followUp' | 'options'> {
  const preview = buildPreviewBody(input);
  return {
    requestText: input.requestText.trim(),
    goal: preview.goal,
    followUp: preview.followUp,
    options: preview.options,
  };
}

/* -------------------------------------------------------------------------- */
/*  The route preview                                                          */
/* -------------------------------------------------------------------------- */

/** What a step is called in the preview line. */
export function stepLabelKey(
  step: Pick<GenerationRouteStep, 'stage' | 'capability'> & { purpose: string | null },
): TranslationKey {
  if (step.stage === 'POST_TEXT') return 'studio.step.POST_TEXT';
  if (step.stage === 'MEDIA_PROMPT') {
    if (step.purpose === 'IMAGE_PROMPT') return 'studio.step.IMAGE_PROMPT';
    if (step.purpose === 'AUDIO_TEXT') return 'studio.step.AUDIO_TEXT';
    return 'studio.step.VIDEO_SCRIPT';
  }
  if (step.capability === 'IMAGE') return 'studio.step.IMAGE';
  if (step.capability === 'AUDIO') return 'studio.step.AUDIO';
  return 'studio.step.VIDEO';
}

/** Whether any step has no connector, so the warning (and the link to fix it) should show. */
export function hasUnroutedStep(steps: GenerationRouteStep[]): boolean {
  return steps.some((step) => step.connector === null);
}

/* -------------------------------------------------------------------------- */
/*  A project's next step                                                      */
/* -------------------------------------------------------------------------- */

/** The kind of media a goal (and a post's follow-up) finally produces; null when it ends at the text. */
export function produceCapability(
  goal: GenerationGoal | null,
  followUp: GenerationFollowUp,
): Exclude<GenerationCapability, 'SCRIPT'> | null {
  switch (goal) {
    case 'IMAGE_PROMPT':
      return 'IMAGE';
    case 'VIDEO_FROM_TEXT':
    case 'VIDEO_FROM_IMAGE':
      return 'VIDEO';
    case 'AUDIO_TEXT':
      return 'AUDIO';
    case 'POST':
      return followUp === 'IMAGE' ? 'IMAGE' : followUp === 'NONE' ? null : 'VIDEO';
    default:
      return null;
  }
}

/** The video input a goal needs from a connector. */
function videoInputFor(goal: GenerationGoal | null, followUp: GenerationFollowUp): 'TEXT' | 'IMAGE' {
  return goal === 'VIDEO_FROM_IMAGE' || (goal === 'POST' && followUp === 'VIDEO_FROM_IMAGE') ? 'IMAGE' : 'TEXT';
}

/**
 * The connectors that could be picked by hand for the media step: active,
 * tested, of the right kind, and (for video) able to take the right input.
 * An empty list on a connector means "any", as everywhere else.
 */
export function compatibleConnectors(
  connectors: GenerationConnector[],
  goal: GenerationGoal | null,
  followUp: GenerationFollowUp,
): GenerationConnector[] {
  const capability = produceCapability(goal, followUp);
  if (!capability) return [];
  const input = videoInputFor(goal, followUp);
  return connectors.filter(
    (c) =>
      c.capability === capability &&
      c.isActive &&
      c.verificationStatus === 'PASSED' &&
      (capability !== 'VIDEO' || (c.videoInputs ?? []).length === 0 || (c.videoInputs ?? []).includes(input)),
  );
}

/** A project's text outputs split into the post itself and the prompt for the media. */
export function splitTextOutputs(chains: OutputChain[]): { post: OutputChain[]; prompt: OutputChain[] } {
  return {
    post: chains.filter((chain) => chain.latest.stage !== 'MEDIA_PROMPT'),
    prompt: chains.filter((chain) => chain.latest.stage === 'MEDIA_PROMPT'),
  };
}

/** The finished media prompt to build from: approved first, then the newest. */
export function pickPromptJob(prompts: OutputChain[]): GenerationJob | null {
  const ready = prompts
    .map((chain) => chain.latest)
    .filter((job) => (job.status === 'DONE' || job.status === 'APPROVED') && (job.resultText ?? '').trim())
    .sort((a, b) => {
      if ((a.status === 'APPROVED') !== (b.status === 'APPROVED')) return a.status === 'APPROVED' ? -1 : 1;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  return ready[0] ?? null;
}
